import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Card, Tab, Nav, Button, Form, Alert } from 'react-bootstrap'
import styled, { keyframes, css } from 'styled-components'
import {
  Eye,
  CheckCircle2,
  XCircle,
  CheckCheck,
  XOctagon,
  ListChecks,
  Info,
  Edit3,
} from 'lucide-react'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import AALReviewSnippetGrid from './AALReviewSnippetGrid'
import ClassificationReviewGrid from './ClassificationReviewGrid'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { ProjectAnnotationType } from '../../../types/models/project'

// Pulsating animation for rejected tab
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
`

const PulsatingNavLink = styled(Nav.Link)<{ $shouldPulsate: boolean }>`
  ${(props) =>
    props.$shouldPulsate &&
    css`
      animation: ${pulse} 2s infinite;
      position: relative;
    `}
`

interface Props {
  contentId: string
  activeTab: string
  onSelectTab: (key: string | null) => void
  untouched: AnnotationDataBoundingBox[]
  accepted: AnnotationDataBoundingBox[]
  rejected: AnnotationDataBoundingBox[]
  selectedBBoxClassIndex?: number
  onSnippetSelect: (box: AnnotationDataBoundingBox) => void
  onAccept: (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => void
  onReject: (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => void
  onBulkAccept?: () => void
  onBulkReject?: () => void
  hoveredBoxIndex?: number
  onHover: (index: number | undefined) => void
}

const PredictionRightPanel: React.FC<Props> = ({
  contentId,
  activeTab,
  onSelectTab,
  untouched,
  accepted,
  rejected,
  selectedBBoxClassIndex,
  onSnippetSelect,
  onAccept,
  onReject,
  onBulkAccept,
  onBulkReject,
  hoveredBoxIndex,
  onHover,
}) => {
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIndices, setSelectedIndices] = useState<number[]>([])
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)

  // Reset selection when tab changes or selection mode is toggled off
  useEffect(() => {
    setSelectedIndices([])
  }, [activeTab, selectionMode])

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const annotationType: ProjectAnnotationType | undefined = project?.annotation_type
  const isClassification = annotationType === 'classification'
  const [rotateLongSideVertical, setRotateLongSideVertical] = React.useState(false)

  // Get current items based on active tab
  const currentItems = useMemo(() => {
    if (activeTab === 'accepted') return accepted
    if (activeTab === 'rejected') return rejected
    return untouched
  }, [activeTab, accepted, rejected, untouched])

  // Toggle individual item selection
  const handleToggleSelect = useCallback((index: number) => {
    setSelectedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }, [])

  // Select all items in current tab
  const handleSelectAll = useCallback(() => {
    if (selectedIndices.length === currentItems.length) {
      setSelectedIndices([])
    } else {
      setSelectedIndices(currentItems.map((_, idx) => idx))
    }
  }, [currentItems, selectedIndices.length])

  // Toggle selection mode
  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev)
    setSelectedIndices([])
  }, [])

  // Toggle individual item selection
  const toggleSelectIndex = useCallback((index: number) => {
    setSelectedIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index)
      } else {
        return [...prev, index]
      }
    })
  }, [])

  // Bulk accept selected items
  const handleAcceptSelected = useCallback(() => {
    const itemsToAccept = selectedIndices.map((idx) => currentItems[idx])
    itemsToAccept.forEach((item) => {
      if (item && onAccept) {
        onAccept(item)
      }
    })
    setSelectedIndices([])
    setSelectionMode(false)
  }, [selectedIndices, currentItems, onAccept])

  // Bulk reject selected items
  const handleRejectSelected = useCallback(() => {
    const itemsToReject = selectedIndices.map((idx) => currentItems[idx])
    itemsToReject.forEach((item) => {
      if (item && onReject) {
        onReject(item)
      }
    })
    setSelectedIndices([])
    setSelectionMode(false)
  }, [selectedIndices, currentItems, onReject])

  // Set indeterminate state on select all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const isIndeterminate =
        selectedIndices.length > 0 && selectedIndices.length < currentItems.length
      selectAllCheckboxRef.current.indeterminate = isIndeterminate
    }
  }, [selectedIndices.length, currentItems.length])

  const allowedTabs = useMemo<string[]>(() => {
    switch (annotationType) {
      case 'bounding_box':
      case 'oriented_bounding_box':
      case 'classification':
        return ['inspect', 'accepted', 'rejected']
      case 'segmentation':
      default:
        return []
    }
  }, [annotationType])

  const effectiveActiveTab = allowedTabs.includes(activeTab) ? activeTab : allowedTabs[0]

  useEffect(() => {
    if (allowedTabs.length === 0) {
      if (activeTab !== '') {
        onSelectTab(null)
      }
      return
    }
    if (effectiveActiveTab && activeTab !== effectiveActiveTab) {
      onSelectTab(effectiveActiveTab)
    }
  }, [activeTab, allowedTabs, effectiveActiveTab, onSelectTab])

  if (allowedTabs.length === 0) {
    return (
      <Card className="bg-dark border-0 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
        <Card.Body
          className="bg-dark flex-grow-1 d-flex flex-column overflow-hidden"
          style={{ minHeight: 0 }}
        />
      </Card>
    )
  }

  return (
    <div
      className="h-100 d-flex flex-column"
      style={{
        minHeight: 0,
        backgroundColor: '#161b22',
      }}
    >
      <div
        className="border-bottom py-2 px-3"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          backgroundColor: '#0d1117',
        }}
      >
        {/* Tab Navigation - Compact Labelbox Style */}
        <Tab.Container activeKey={effectiveActiveTab} onSelect={onSelectTab}>
          <Nav variant="tabs" className="w-100 border-0 d-flex align-items-center">
            {allowedTabs.includes('inspect') && (
              <Nav.Item>
                <Nav.Link
                  eventKey="inspect"
                  className={`px-3 py-2 border-0 ${
                    effectiveActiveTab === 'inspect' ? 'active' : ''
                  }`}
                  disabled={untouched.length === 0}
                  style={
                    untouched.length === 0
                      ? {
                          color: '#484f58',
                          backgroundColor: 'transparent',
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        }
                      : effectiveActiveTab === 'inspect'
                      ? {
                          backgroundColor: '#161b22',
                          color: '#58a6ff',
                          borderBottom: '2px solid #58a6ff',
                        }
                      : {
                          color: '#8b949e',
                          backgroundColor: 'transparent',
                        }
                  }
                >
                  <div className="d-flex align-items-center gap-2">
                    <Eye size={16} />
                    <span className="small">Inspect</span>
                    <span className="badge bg-warning text-dark">{untouched.length}</span>
                  </div>
                </Nav.Link>
              </Nav.Item>
            )}
            {allowedTabs.includes('accepted') && (
              <Nav.Item>
                <Nav.Link
                  eventKey="accepted"
                  className={`px-3 py-2 border-0 ${
                    effectiveActiveTab === 'accepted' ? 'active' : ''
                  }`}
                  disabled={accepted.length === 0}
                  style={
                    accepted.length === 0
                      ? {
                          color: '#484f58',
                          backgroundColor: 'transparent',
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        }
                      : effectiveActiveTab === 'accepted'
                      ? {
                          backgroundColor: '#161b22',
                          color: '#58a6ff',
                          borderBottom: '2px solid #58a6ff',
                        }
                      : {
                          color: '#8b949e',
                          backgroundColor: 'transparent',
                        }
                  }
                >
                  <div className="d-flex align-items-center gap-2">
                    <CheckCircle2 size={16} />
                    <span className="small">Accepted</span>
                    <span className="badge bg-success">{accepted.length}</span>
                  </div>
                </Nav.Link>
              </Nav.Item>
            )}
            {allowedTabs.includes('rejected') && (
              <Nav.Item>
                <PulsatingNavLink
                  eventKey="rejected"
                  className={`px-3 py-2 border-0 ${
                    effectiveActiveTab === 'rejected' ? 'active' : ''
                  }`}
                  disabled={rejected.length === 0}
                  $shouldPulsate={rejected.length > 0 && effectiveActiveTab !== 'rejected'}
                  style={
                    rejected.length === 0
                      ? {
                          color: '#484f58',
                          backgroundColor: 'transparent',
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        }
                      : effectiveActiveTab === 'rejected'
                      ? {
                          backgroundColor: '#161b22',
                          color: '#58a6ff',
                          borderBottom: '2px solid #58a6ff',
                        }
                      : {
                          color: '#8b949e',
                          backgroundColor: 'transparent',
                        }
                  }
                >
                  <div className="d-flex align-items-center gap-2">
                    <XCircle size={16} />
                    <span className="small">Rejected</span>
                    <span className="badge bg-danger">{rejected.length}</span>
                  </div>
                </PulsatingNavLink>
              </Nav.Item>
            )}
            {/* Bulk Selection Toggle - Far Right */}
            <div className="ms-auto d-flex align-items-center gap-2">
              {annotationType === 'oriented_bounding_box' && (
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="rotateSnippetsSwitchPred"
                    checked={rotateLongSideVertical}
                    onChange={(e) => setRotateLongSideVertical(e.target.checked)}
                  />
                  <label
                    className="form-check-label text-light small"
                    htmlFor="rotateSnippetsSwitchPred"
                    style={{ fontSize: '0.75rem' }}
                  >
                    Rotate snippets
                  </label>
                </div>
              )}
              <Button
                size="sm"
                onClick={handleToggleSelectionMode}
                className="d-flex align-items-center gap-2"
                style={{
                  fontSize: '0.8125rem',
                  backgroundColor: selectionMode ? '#9333ea' : 'transparent',
                  borderColor: selectionMode ? '#9333ea' : '#58a6ff',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  color: selectionMode ? '#ffffff' : '#58a6ff',
                  transition: 'all 0.3s ease',
                  fontWeight: selectionMode ? '600' : '400',
                  boxShadow: selectionMode ? '0 0 12px rgba(147, 51, 234, 0.4)' : 'none',
                }}
              >
                <ListChecks size={16} />
                <span>{selectionMode ? 'Exit Bulk' : 'Bulk'}</span>
              </Button>
            </div>
          </Nav>
        </Tab.Container>
      </div>

      {/* Selection Controls & Bulk Action Buttons */}
      {selectionMode && (
        <div
          className="border-bottom py-2 px-3"
          style={{
            borderColor: 'rgba(255, 255, 255, 0.1)',
            backgroundColor: '#0d1117',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            {/* Select All Checkbox */}
            <Form.Check
              ref={selectAllCheckboxRef}
              type="checkbox"
              id="selectAllCheckbox"
              checked={selectedIndices.length === currentItems.length && currentItems.length > 0}
              onChange={handleSelectAll}
              label={
                <span className="text-light small">
                  {selectedIndices.length === currentItems.length && currentItems.length > 0
                    ? 'Deselect All'
                    : selectedIndices.length > 0
                    ? `Selected ${selectedIndices.length} of ${currentItems.length}`
                    : 'Select All'}
                </span>
              }
            />

            {/* Action Buttons - Only show when items are selected */}
            {selectedIndices.length > 0 && (
              <div className="ms-auto d-flex gap-2">
                {(activeTab === 'inspect' || activeTab === 'rejected') && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAcceptSelected}
                    className="d-flex align-items-center gap-2"
                    style={{ fontSize: '0.8125rem' }}
                  >
                    <CheckCircle2 size={16} />
                    <span>Accept ({selectedIndices.length})</span>
                  </Button>
                )}

                {(activeTab === 'inspect' || activeTab === 'accepted') && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleRejectSelected}
                    className="d-flex align-items-center gap-2"
                    style={{ fontSize: '0.8125rem' }}
                  >
                    <XCircle size={16} />
                    <span>Reject ({selectedIndices.length})</span>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className="flex-grow-1 d-flex flex-column overflow-hidden"
        style={{ minHeight: 0, backgroundColor: '#161b22' }}
      >
        {/* Educational Banner for Rejected Tab */}
        {activeTab === 'rejected' && rejected.length > 0 && !selectionMode && (
          <Alert
            variant="info"
            className="m-2 mb-0 py-2 px-3 d-flex align-items-start gap-2"
            style={{ fontSize: '0.8125rem' }}
          >
            <Info size={16} className="flex-shrink-0 mt-1" />
            <div>
              <strong className="d-block mb-1">💡 Help Your Model Learn</strong>
              <span>
                {isClassification
                  ? 'These rejected predictions should be corrected. Click items to review and fix incorrect classifications.'
                  : 'These rejected predictions should be corrected. Click items to open the editor and fix labels, adjust bounding boxes, or add missed objects.'}
              </span>
            </div>
          </Alert>
        )}

        <Tab.Container activeKey={effectiveActiveTab} onSelect={onSelectTab}>
          <Tab.Content className="flex-grow-1 overflow-auto h-100" style={{ minHeight: 0 }}>
            {allowedTabs.includes('inspect') && (
              <Tab.Pane eventKey="inspect" className="h-100">
                {isClassification ? (
                  <ClassificationReviewGrid
                    items={untouched}
                    selectedBBoxClassIndex={selectedBBoxClassIndex}
                    showAcceptButton={!selectionMode}
                    showRejectButton={!selectionMode}
                    onAccept={onAccept}
                    onReject={onReject}
                    onSelect={onSnippetSelect}
                    selectionMode={selectionMode}
                    selectedIndices={selectedIndices}
                    onToggleSelect={toggleSelectIndex}
                  />
                ) : (
                  <AALReviewSnippetGrid
                    contentId={contentId}
                    items={untouched}
                    selectedBBoxClassIndex={selectedBBoxClassIndex}
                    showConfidence={true}
                    showAcceptButton={!selectionMode}
                    showRejectButton={!selectionMode}
                    onAccept={onAccept}
                    onReject={onReject}
                    hoveredBoxIndex={hoveredBoxIndex}
                    onHover={onHover}
                    onSelect={onSnippetSelect}
                    selectionMode={selectionMode}
                    selectedIndices={selectedIndices}
                    onToggleSelect={toggleSelectIndex}
                    rotateLongSideVertical={rotateLongSideVertical}
                  />
                )}
              </Tab.Pane>
            )}
            {allowedTabs.includes('accepted') && (
              <Tab.Pane eventKey="accepted" className="h-100">
                {isClassification ? (
                  <ClassificationReviewGrid
                    items={accepted}
                    selectedBBoxClassIndex={selectedBBoxClassIndex}
                    showAcceptButton={false}
                    showRejectButton={!selectionMode}
                    onReject={onReject}
                    onSelect={onSnippetSelect}
                    selectionMode={selectionMode}
                    selectedIndices={selectedIndices}
                    onToggleSelect={toggleSelectIndex}
                  />
                ) : (
                  <AALReviewSnippetGrid
                    items={accepted}
                    contentId={contentId}
                    selectedBBoxClassIndex={selectedBBoxClassIndex}
                    showConfidence={true}
                    showAcceptButton={false}
                    showRejectButton={!selectionMode}
                    onReject={onReject}
                    hoveredBoxIndex={hoveredBoxIndex}
                    onHover={onHover}
                    onSelect={onSnippetSelect}
                    selectionMode={selectionMode}
                    selectedIndices={selectedIndices}
                    onToggleSelect={toggleSelectIndex}
                    rotateLongSideVertical={rotateLongSideVertical}
                  />
                )}
              </Tab.Pane>
            )}
            {allowedTabs.includes('rejected') && (
              <Tab.Pane eventKey="rejected" className="h-100">
                {isClassification ? (
                  <ClassificationReviewGrid
                    items={rejected}
                    selectedBBoxClassIndex={selectedBBoxClassIndex}
                    showAcceptButton={!selectionMode}
                    showRejectButton={false}
                    onAccept={onAccept}
                    onSelect={onSnippetSelect}
                    selectionMode={selectionMode}
                    selectedIndices={selectedIndices}
                    onToggleSelect={toggleSelectIndex}
                  />
                ) : (
                  <AALReviewSnippetGrid
                    items={rejected}
                    contentId={contentId}
                    selectedBBoxClassIndex={selectedBBoxClassIndex}
                    showConfidence={true}
                    showAcceptButton={!selectionMode}
                    showRejectButton={false}
                    onAccept={onAccept}
                    hoveredBoxIndex={hoveredBoxIndex}
                    onHover={onHover}
                    onSelect={onSnippetSelect}
                    selectionMode={selectionMode}
                    selectedIndices={selectedIndices}
                    onToggleSelect={toggleSelectIndex}
                    rotateLongSideVertical={rotateLongSideVertical}
                  />
                )}
              </Tab.Pane>
            )}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  )
}

export default PredictionRightPanel
